/**
 * Map a row from GET /api/admin-dash/board-chemistry/ into the shape
 * expected by BoardDynamicsOptimization (1–5 style averages + metadata).
 *
 * Backend fields (example): total_score, decision_making_score, collaboration_score,
 * sustainability_score, communication_score, answers[], assessment_date, created_at.
 */

const UI_CATEGORIES = [
  ["collaboration", "collaboration_score"],
  ["decision-making", "decision_making_score"],
  ["communication", "communication_score"],
  ["sustainability", "sustainability_score"],
];

export function mapBoardChemistryApiToDynamicsDisplay(latest) {
  if (!latest || typeof latest !== "object") {
    return null;
  }

  const answers = Array.isArray(latest.answers) ? latest.answers : [];

  // Legacy / alternate shape: pre-aggregated 1–5 scores object, no per-answer rows
  if (
    answers.length === 0 &&
    latest.scores &&
    typeof latest.scores === "object" &&
    Object.keys(latest.scores).length > 0
  ) {
    const scores = { ...latest.scores };
    let overallScore =
      typeof latest.overall_score === "number" && !Number.isNaN(latest.overall_score)
        ? latest.overall_score
        : typeof latest.overallScore === "number" && !Number.isNaN(latest.overallScore)
          ? latest.overallScore
          : null;
    if (overallScore == null) {
      const vals = Object.values(scores).filter(
        (x) => typeof x === "number" && !Number.isNaN(x),
      );
      overallScore = vals.length
        ? vals.reduce((a, b) => a + b, 0) / vals.length
        : 0;
    }
    const completedAt =
      latest.created_at ||
      latest.assessment_date ||
      latest.completed_at ||
      new Date().toISOString();
    return {
      completedAt,
      overallScore,
      scores,
      boardMembers: latest.board_members || latest.boardMembers || [],
      recommendations: latest.recommendations || [],
    };
  }
  const sums = {};
  const counts = {};

  for (const a of answers) {
    const cat = a.category;
    if (!cat) continue;
    sums[cat] = (sums[cat] || 0) + (Number(a.score) || 0);
    counts[cat] = (counts[cat] || 0) + 1;
  }

  const scores = {};
  for (const [uiKey, snakeSum] of UI_CATEGORIES) {
    if (counts[uiKey]) {
      scores[uiKey] = sums[uiKey] / counts[uiKey];
    } else {
      const raw = latest[snakeSum];
      scores[uiKey] =
        typeof raw === "number" && !Number.isNaN(raw) ? raw : 0;
    }
  }

  let overallScore = null;
  if (
    typeof latest.overall_score === "number" &&
    !Number.isNaN(latest.overall_score)
  ) {
    overallScore = latest.overall_score;
  } else if (
    typeof latest.overallScore === "number" &&
    !Number.isNaN(latest.overallScore)
  ) {
    overallScore = latest.overallScore;
  } else if (
    answers.length > 0 &&
    typeof latest.total_score === "number" &&
    !Number.isNaN(latest.total_score)
  ) {
    overallScore = latest.total_score / answers.length;
  }

  if (overallScore == null) {
    const vals = Object.values(scores).filter(
      (x) => typeof x === "number" && !Number.isNaN(x),
    );
    overallScore = vals.length
      ? vals.reduce((a, b) => a + b, 0) / vals.length
      : 0;
  }

  const completedAt =
    latest.created_at ||
    latest.assessment_date ||
    latest.completed_at ||
    new Date().toISOString();

  const boardMembersRaw = latest.board_members || latest.boardMembers || [];
  const boardMembers = Array.isArray(boardMembersRaw)
    ? boardMembersRaw.map((m) => ({
        name: m.name,
        collaboration: Number(m.collaboration ?? m.collaboration_score) || 0,
        decisionMaking: Number(m.decision_making ?? m.decision_making_score) || 0,
        communication: Number(m.communication ?? m.communication_score) || 0,
        sustainability: Number(m.sustainability ?? m.sustainability_score) || 0,
        overallScore: Number(m.overall_score ?? m.overallScore) || 0,
      }))
    : [];

  const recRaw = latest.recommendations || [];
  const recommendations = Array.isArray(recRaw)
    ? recRaw.map((r) => ({
        area: r.area,
        priority: r.priority,
        suggestion: r.suggestion,
        implementation: r.implementation,
        timeframe: r.timeframe,
        meetingImprovement:
          r.meeting_improvement || r.meetingImprovement || "",
      }))
    : [];

  return {
    completedAt,
    overallScore,
    scores,
    boardMembers,
    recommendations,
  };
}
